import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { getApi } from '../../Services/apiService';

const DEFAULT_MONTHLY = [
    { month: 'Jan', users: 0 }, { month: 'Feb', users: 0 }, { month: 'Mar', users: 0 }, { month: 'Apr', users: 0 },
    { month: 'May', users: 0 }, { month: 'Jun', users: 0 }, { month: 'Jul', users: 0 }, { month: 'Aug', users: 0 },
    { month: 'Sep', users: 0 }, { month: 'Oct', users: 0 }, { month: 'Nov', users: 0 }, { month: 'Dec', users: 0 }
];
const DEFAULT_PLAN = [
    { name: 'Premium', value: 0 },
    { name: 'Free', value: 0 }
];

const TIME_RANGE_TO_DURATION = {
    '12 Months': '12_months',
    '6 Months': '6_months',
    '30 Days': '30_days',
    '7 Days': '7_days'
};

const Dashboard = () => {
    const [timeRange, setTimeRange] = useState('12 Months');
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalPets: 0,
        premiumUsers: 0,
        lostPetReport: 0
    });
    const [monthlyData, setMonthlyData] = useState(DEFAULT_MONTHLY);
    const [planData, setPlanData] = useState(DEFAULT_PLAN);

    useEffect(() => {
        const fetchDashboard = async () => {
            setLoading(true);
            try {
                const duration = TIME_RANGE_TO_DURATION[timeRange] || '12_months';
                const response = await getApi(`/api/v1/admin/dashboard?period=${encodeURIComponent(duration)}`);
                const data = response?.data ?? response ?? {};
                setStats({
                    totalUsers: data.totalUsers ?? 0,
                    totalPets: data.totalPets ?? 0,
                    premiumUsers: data.totalPremiumUsers ?? 0,
                    lostPetReport: data.totalLostReports ?? 0
                });
                const overview = data.usersMonthlyOverview;
                if (overview?.labels && Array.isArray(overview.data)) {
                    setMonthlyData(overview.labels.map((label, i) => ({
                        month: label,
                        users: Number(overview.data[i] ?? 0)
                    })));
                }
                const distribution = data.plans?.distribution;
                if (Array.isArray(distribution) && distribution.length) {
                    setPlanData(distribution.map((p) => ({
                        name: p.name ?? '',
                        value: Number(p.value ?? 0)
                    })));
                }
            } catch (err) {
                console.error('Error fetching dashboard:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchDashboard();
    }, [timeRange]);

    const PLAN_COLORS = ['#2FB6B2', '#F7D774'];

    const PlanTooltip = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const item = payload[0];
        return (
            <div className="plan-chart-tooltip">
                <div className="plan-tooltip-name">{item.name}:</div>
                <div className="plan-tooltip-value">Users: {item.value?.toLocaleString()}</div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className='RightSideDiv'>
                <div className="dashboard-stats" style={{ justifyContent: 'center', padding: '2rem' }}>
                    <p>Loading dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className='RightSideDiv'>
            {/* Summary Cards */}
            <div className="dashboard-stats">
                <div className="stat-card">
                    <div className="stat-icon">
                        <img src="/assets/images/dashboardusers.svg" alt="" aria-hidden />
                    </div>
                    <h3 className="stat-label">Total Users</h3>
                    <h2 className="stat-number">{stats.totalUsers}</h2>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <img src="/assets/images/dashboardusers.svg" alt="" aria-hidden />
                    </div>
                    <h3 className="stat-label">Total Pets</h3>
                    <h2 className="stat-number">{stats.totalPets}</h2>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <img src="/assets/images/dashboardusers.svg" alt="" aria-hidden />
                    </div>
                    <h3 className="stat-label">Premium Users</h3>
                    <h2 className="stat-number">{stats.premiumUsers}</h2>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <img src="/assets/images/dashboardusers.svg" alt="" aria-hidden />
                    </div>
                    <h3 className="stat-label">Lost Pet Report</h3>
                    <h2 className="stat-number">{stats.lostPetReport}</h2>
                </div>
            </div>

            {/* Charts Section */}
            <div className="dashboard-charts">
                {/* Users Monthly Overview Chart */}
                <div className="chart-container">
                    <div className="chart-header">
                        <h3>Users (Monthly Overview)</h3>
                        <div className="chart-filters">
                            <button 
                                className={timeRange === '12 Months' ? 'active' : ''} 
                                onClick={() => setTimeRange('12 Months')}
                            >
                                12 Months
                            </button>
                            <button 
                                className={timeRange === '6 Months' ? 'active' : ''} 
                                onClick={() => setTimeRange('6 Months')}
                            >
                                6 Months
                            </button>
                            <button 
                                className={timeRange === '30 Days' ? 'active' : ''} 
                                onClick={() => setTimeRange('30 Days')}
                            >
                                30 Days
                            </button>
                            <button 
                                className={timeRange === '7 Days' ? 'active' : ''} 
                                onClick={() => setTimeRange('7 Days')}
                            >
                                7 Days
                            </button>
                        </div>
                    </div>
                    <div className="chart-content">
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={monthlyData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <Tooltip />
                                <Line 
                                    type="monotone" 
                                    dataKey="users" 
                                    stroke="#34c7a5" 
                                    strokeWidth={2}
                                    dot={{ fill: '#34c7a5', r: 4 }}
                                    activeDot={{ r: 6 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Plan Distribution Chart */}
                <div className="chart-container plan-chart-container">
                    <div className="chart-header">
                        <h3>Plan</h3>
                    </div>
                    <div className="chart-content plan-chart-content">
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={planData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={false}
                                    outerRadius={100}
                                    dataKey="value"
                                >
                                    {planData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={PLAN_COLORS[index % PLAN_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip content={<PlanTooltip />} />
                                <Legend layout="vertical" align="left" verticalAlign="middle" wrapperStyle={{ paddingLeft: 0 }} iconType="circle" />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
